// @ts-strict-ignore
/**
 * Hook to generate policy context document for LLM summary enrichment.
 * Uses Redux workflow state (initialized via useInitializeWorkflowRedux) and
 * threshold results to generate a rich rule document.
 */
import {
  useContext,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { UserContext } from '~/dashboard/context';
import { useAppSelector } from '~/store/reduxHooks';
import {
  selectWorkflowData,
  selectServices,
  selectCustomLists,
  selectLegacyMappings,
} from '~/dashboard/views/workflows/store/redux/selectors';
import { useInitializeWorkflowRedux } from '~/dashboard/views/workflows/editor/hooks/useInitializeWorkflowRedux';
import { useThresholdResults } from '~/entities/evaluations/TagRuleModal/hooks';
import { generateWorkflowDocumentationWithResults } from '~/dashboard/views/workflows/service/admin/docGenerator';
import type { Dimension } from '~/application-review/redux/types';
import { captureExceptionsToSentry } from '~/helpers/exceptionHandler';

// Stable empty array reference to avoid re-renders
const EMPTY_DIMENSIONS: Dimension[] = [];

interface UsePolicyContextProps {
  entityToken: string;
  evaluationToken: string;
  workflowToken: string | null;
  workflowVersion: number | null;
  isPortfolioEvaluation?: boolean;
  dimensions?: Dimension[];
  enabled?: boolean;
}

interface PolicyContextResult {
  ruleDocument: string | null;
  isLoading: boolean;
  isError: boolean;
  sendToIframe: (iframeRef: React.RefObject<HTMLIFrameElement>) => void;
}

export const usePolicyContext = ({
  entityToken,
  evaluationToken,
  workflowToken,
  workflowVersion,
  isPortfolioEvaluation = false,
  dimensions,
  enabled = true,
}: UsePolicyContextProps): PolicyContextResult => {
  const { permissions, featureFlags } = useContext(UserContext);

  const canViewRules = !!permissions['READ:ApplicationVersions:null'];

  // Use stable empty array if dimensions is undefined/empty
  const stableDimensions = dimensions && dimensions.length > 0 ? dimensions : EMPTY_DIMENSIONS;

  // Track if we've sent the message to avoid duplicates
  const hasSentMessage = useRef(false);

  // Read current workflow data from Redux to check if we need to reinitialize
  const workflowData = useAppSelector(selectWorkflowData);
  const services = useAppSelector(selectServices);
  const customLists = useAppSelector(selectCustomLists);
  const legacyMappings = useAppSelector(selectLegacyMappings);

  // Determine if we need to initialize workflow Redux state
  const shouldInitializeWorkflow = useMemo(() => Boolean(
    enabled
    && workflowToken
    && workflowVersion
    && (
      !workflowData?.token
      || !workflowData?.publicVersion
      || workflowData?.token !== workflowToken
      || workflowData?.publicVersion !== workflowVersion
    ),
  ), [enabled, workflowData?.token, workflowData?.publicVersion, workflowToken, workflowVersion]);

  // Initialize workflow data in Redux - this handles fetching and transformation
  useInitializeWorkflowRedux({
    workflowToken,
    versionId: workflowVersion,
    featureFlags,
    enabled: shouldInitializeWorkflow,
    reinitialize: true,
  });

  // Check if workflow is ready (either already in Redux or just initialized)
  const workflowReady = useMemo(() => (
    workflowData?.actions
    && workflowData?.token === workflowToken
    && workflowData?.publicVersion === workflowVersion
  ), [workflowData, workflowToken, workflowVersion]);

  // Fetch threshold results
  const {
    data: thresholdResultsData,
    isLoading: thresholdResultsLoading,
    isError: thresholdResultsError,
  } = useThresholdResults({
    entityToken,
    evaluationToken,
    canViewRules,
    isPortfolioEvaluation,
    dimensions: stableDimensions,
  });

  // Generate the rule document using transformed Redux data
  const ruleDocument = useMemo(() => {
    if (!enabled || !workflowReady || !workflowData) {
      return null;
    }

    try {
      // eslint-disable-next-line no-underscore-dangle
      const thresholdResults = thresholdResultsData?._embedded?.actions;

      // Generate the document with transformed Redux data, threshold results, and legacy mappings
      return generateWorkflowDocumentationWithResults(
        workflowData,
        thresholdResults,
        services,
        customLists,
        legacyMappings,
      );
    } catch (error) {
      captureExceptionsToSentry(error, { context: 'usePolicyContext: Error generating rule document' });
      return null;
    }
  }, [enabled, workflowReady, workflowData,
    thresholdResultsData, services, customLists, legacyMappings]);

  // Function to send policy context to iframe
  const sendToIframe = useCallback((iframeRef: React.RefObject<HTMLIFrameElement>) => {
    if (!ruleDocument || hasSentMessage.current) {
      return;
    }

    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) {
      return;
    }

    iframe.contentWindow.postMessage(
      {
        source: 'daffy',
        type: 'policyContext',
        data: { ruleDocument },
      },
      window.location.origin,
    );

    hasSentMessage.current = true;
  }, [ruleDocument]);

  // Reset sent flag when dependencies change
  useEffect(() => {
    hasSentMessage.current = false;
  }, [entityToken, evaluationToken, workflowToken, workflowVersion]);

  const isLoading = shouldInitializeWorkflow || thresholdResultsLoading;
  const isError = thresholdResultsError;

  return {
    ruleDocument,
    isLoading,
    isError,
    sendToIframe,
  };
};
